import axios from 'axios';

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For local development, use localhost
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // In development, try to use port 5000 first (default)
    // If the frontend is on a different port, it means the server auto-assigned a port
    // In that case, use the same port as the frontend
    const apiPort = port || '5000'; // Use current port, default to 5000 if no port
    const apiUrl = `http://${hostname}:${apiPort}/api`;
    return apiUrl;
  }
  
  // For production, use the production URL
  const prodUrl = 'https://evoucher.sos-palestine.org/api';
  return prodUrl;
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and project ID to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && token !== 'undefined' && token !== 'null') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add project ID from localStorage if available
  const projectId = localStorage.getItem('currentProjectId');
  if (projectId && projectId !== 'undefined' && projectId !== 'null') {
    config.headers['X-Project-ID'] = projectId;
  }
  
  return config;
});

// Handle token expiration and 404 errors - NEVER show 404 to user
api.interceptors.response.use(
  (response) => {
    // Log successful API calls in production for debugging
    if (process.env.NODE_ENV === 'production') {
      console.log('✅ API Success:', response.config.method?.toUpperCase(), response.config.url, response.status);
    }
    return response;
  },
  (error) => {
    // Enhanced error logging for production debugging
    console.error('❌ API Error:', {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    // Handle 404 errors - retry the request and NEVER show 404 to user
    // EXCEPT for search endpoints where 404 means "no results found" - don't retry or convert
    const isSearchEndpoint = error.config?.url?.includes('/beneficiaries/search');
    
    if (error.response?.status === 404 || !error.response) {
      // For search endpoints, allow 404 to pass through (means "no matching values")
      if (isSearchEndpoint && error.response?.status === 404) {
        // Don't retry or convert - let the component handle "No matching values"
        return Promise.reject(error);
      }
      
      // If no response (network error) or 404, retry
      if (!error.config._retry) {
        error.config._retry = true;
        console.warn('⚠️ Request failed, retrying...', error.config.url);
        
        // Retry once after a short delay
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            api.request(error.config)
              .then(resolve)
              .catch((retryError) => {
                // If retry also fails, convert to 503 (service unavailable) instead of 404
                console.error('❌ Request failed after retry:', error.config.url);
                const friendlyError = {
                  ...retryError,
                  response: {
                    ...retryError.response,
                    status: 503,
                    data: { 
                      error: 'Service temporarily unavailable. Please refresh the page or try again in a moment.',
                      originalStatus: retryError.response?.status || 'network_error'
                    }
                  }
                };
                reject(friendlyError);
              });
          }, 1000);
        });
      } else {
        // Already retried, convert 404 to 503 - NEVER show 404
        error.response = {
          ...error.response,
          status: 503,
          data: { 
            error: 'Service temporarily unavailable. Please refresh the page.',
            originalStatus: 404
          }
        };
      }
    }
    
    // Also handle network errors (no response) - convert to 503
    if (!error.response) {
      error.response = {
        status: 503,
        data: { 
          error: 'Connection issue. Please check your internet connection and try again.',
          originalStatus: 'network_error'
        }
      };
    }
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // For 503 errors (service unavailable), show friendly message
    if (error.response?.status === 503) {
      console.warn('⚠️ Service unavailable, showing friendly message to user');
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
};

// Projects API
export const projectsAPI = {
  getAll: () => api.get('/projects'),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  addUser: (projectId, userId, role) => api.post(`/projects/${projectId}/users`, { userId, role }),
  getUsers: (projectId) => api.get(`/projects/${projectId}/users`),
  removeUser: (projectId, userId) => api.delete(`/projects/${projectId}/users/${userId}`),
};

// Beneficiaries API
export const beneficiariesAPI = {
  getAll: () => api.get('/beneficiaries'),
  create: (data) => api.post('/beneficiaries', data),
  update: (id, data) => api.put(`/beneficiaries/${id}`, data),
  delete: (id) => api.delete(`/beneficiaries/${id}`),
  search: (searchInput) => api.post('/beneficiaries/search', { searchInput }),
  import: (file) => {
    const formData = new FormData();
    formData.append('csvFile', file);
    return api.post('/beneficiaries/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  export: () => api.get('/beneficiaries/export', { responseType: 'blob' }),
};

// Redemptions API
export const redemptionsAPI = {
  create: (data) => api.post('/redemptions', data),
  getByBeneficiary: (beneficiaryId) => api.get(`/redemptions/${beneficiaryId}`),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (id, currentPassword, newPassword) => 
    api.post(`/users/${id}/change-password`, { currentPassword, newPassword }),
};

// Audit Log API
export const auditAPI = {
  getAll: () => api.get('/audit-log'),
};

// Vendor Stats API
export const vendorAPI = {
  getStats: (vendorId) => api.get(`/vendor-stats/${vendorId}`),
  getAll: () => api.get('/vendors'),
};

// SMS API
export const smsAPI = {
  sendVerification: (phoneNumber, code) => 
    api.post('/sms/send-verification', { phoneNumber, code }),
  
  sendReceipt: (phoneNumber, beneficiaryName, couponCount, vendorName) => 
    api.post('/sms/send-receipt', { phoneNumber, beneficiaryName, couponCount, vendorName }),
  
  sendAllVouchersUsed: (phoneNumber) => 
    api.post('/sms/send-all-vouchers-used', { phoneNumber }),
};

export default api;
